import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import type { HkdInfo, S1aEntry } from "../api";

const THIN_BORDER: Partial<ExcelJS.Borders> = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
};

function formatDateVi(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const DEFAULT_DESCRIPTION = "Doanh thu bán hàng hóa, dịch vụ";

/** Sắp xếp theo ngày tăng dần; cùng ngày giữ thứ tự như mảng gốc (stable). */
function sortEntriesByDate(entries: S1aEntry[]): S1aEntry[] {
  return [...entries].sort((a, b) => a.entry_date.localeCompare(b.entry_date));
}

function entryDescription(entry: S1aEntry): string {
  return entry.description?.trim() || DEFAULT_DESCRIPTION;
}

function setCell(
  ws: ExcelJS.Worksheet,
  row: number,
  col: number,
  value: ExcelJS.CellValue,
  opts?: { bold?: boolean; align?: Partial<ExcelJS.Alignment>; border?: boolean; numFmt?: string }
) {
  const cell = ws.getCell(row, col);
  cell.value = value;
  if (opts?.bold) cell.font = { bold: true };
  if (opts?.align) cell.alignment = opts.align;
  if (opts?.border) cell.border = THIN_BORDER;
  if (opts?.numFmt) cell.numFmt = opts.numFmt;
  return cell;
}

export async function exportS1aXlsx(params: {
  hkd: HkdInfo;
  entries: S1aEntry[];
  year: number;
  quarter?: number;
}): Promise<void> {
  const { hkd, entries, year, quarter } = params;
  const sorted = sortEntriesByDate(entries);
  const periodTotal = sorted.reduce((s, e) => s + (e.revenue || 0), 0);

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("S1a-HKD");

  ws.getColumn(1).width = 18;
  ws.getColumn(2).width = 45;
  ws.getColumn(3).width = 18;

  let row = 1;

  // Mẫu số — góc phải
  ws.mergeCells(row, 1, row, 2);
  setCell(ws, row, 3, "Mẫu số S1a-HKD", { bold: true, align: { horizontal: "right" } });
  row++;
  ws.mergeCells(row, 1, row, 2);
  setCell(
    ws,
    row,
    3,
    "(Ban hành kèm theo Thông tư số 152/2025/TT-BTC ngày 31/12/2025 của Bộ Tài chính)",
    { align: { horizontal: "right", wrapText: true } }
  );
  row += 2;

  // Khối thông tin HKD — trái
  const infoLines: string[] = [
    `Tên hộ kinh doanh: ${hkd.name || ""}`,
    `Mã số thuế: ${hkd.tax_code || ""}`,
    `Địa chỉ: ${hkd.address || ""}`,
  ];
  if (hkd.business_line?.trim()) infoLines.push(`Ngành nghề: ${hkd.business_line.trim()}`);
  if (hkd.business_location?.trim()) infoLines.push(`Địa điểm kinh doanh: ${hkd.business_location.trim()}`);

  for (const line of infoLines) {
    ws.mergeCells(row, 1, row, 3);
    setCell(ws, row, 1, line, { align: { horizontal: "left" } });
    row++;
  }
  row++;

  // Tiêu đề giữa
  ws.mergeCells(row, 1, row, 3);
  setCell(ws, row, 1, "SỔ DOANH THU BÁN HÀNG HÓA, DỊCH VỤ", {
    bold: true,
    align: { horizontal: "center", vertical: "middle" },
  });
  row++;

  // Kỳ báo cáo
  ws.mergeCells(row, 1, row, 3);
  const periodLabel = quarter ? `Quý ${quarter}/${year}` : `Năm ${year}`;
  setCell(ws, row, 1, periodLabel, { align: { horizontal: "center" } });
  row += 2;

  setCell(ws, row, 1, "Ngày, tháng ghi sổ", { bold: true, align: { horizontal: "center" }, border: true });
  setCell(ws, row, 2, "Diễn giải", { bold: true, align: { horizontal: "center" }, border: true });
  setCell(ws, row, 3, "Số tiền", { bold: true, align: { horizontal: "center" }, border: true });
  row++;
  setCell(ws, row, 1, "(A)", { align: { horizontal: "center" }, border: true });
  setCell(ws, row, 2, "(B)", { align: { horizontal: "center" }, border: true });
  setCell(ws, row, 3, "(1)", { align: { horizontal: "center" }, border: true });
  row++;

  for (const entry of sorted) {
    setCell(ws, row, 1, formatDateVi(entry.entry_date), { border: true, align: { horizontal: "center" } });
    setCell(ws, row, 2, entryDescription(entry), { border: true });
    setCell(ws, row, 3, entry.revenue || 0, { border: true, align: { horizontal: "right" }, numFmt: "#,##0" });
    row++;
  }

  // Tổng cộng
  setCell(ws, row, 1, "", { border: true });
  setCell(ws, row, 2, "Tổng cộng", { bold: true, border: true, align: { horizontal: "center" } });
  setCell(ws, row, 3, periodTotal, { bold: true, border: true, align: { horizontal: "right" }, numFmt: "#,##0" });
  row += 3;

  // Khối ký — phải
  const now = new Date();
  ws.mergeCells(row, 1, row, 3);
  setCell(ws, row, 1, `Ngày ${now.getDate()} tháng ${now.getMonth() + 1} năm ${now.getFullYear()}`, {
    align: { horizontal: "right" },
  });
  row += 2;

  setCell(ws, row, 2, "NGƯỜI LẬP SỔ", { bold: true, align: { horizontal: "center" } });
  setCell(ws, row, 3, "CHỦ HỘ KINH DOANH", { bold: true, align: { horizontal: "center" } });
  row += 4;
  setCell(ws, row, 2, hkd.preparer?.trim() || "", { align: { horizontal: "center" } });
  setCell(ws, row, 3, hkd.owner?.trim() || "", { align: { horizontal: "center" } });
  row++;
  setCell(ws, row, 2, "(Ký, họ tên)", { align: { horizontal: "center" } });
  setCell(ws, row, 3, "(Ký, họ tên)", { align: { horizontal: "center" } });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const filename = quarter ? `S1a-HKD_Q${quarter}_${year}.xlsx` : `S1a-HKD_${year}.xlsx`;
  saveAs(blob, filename);
}
